import {
  BarVisualizer,
  RoomAudioRenderer,
  useAudioPlayback,
  useDataChannel,
  useLocalParticipant,
  useTranscriptions,
  useVoiceAssistant,
} from "@livekit/components-react";
import { Loader2, Mic, MicOff, PhoneOff, Video, VideoOff, Volume2 } from "lucide-react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import type { TranscriptRow } from "./demo-api";
import type { PhoneAudio } from "./phone-audio";
import { SelfView } from "./SelfView";
import { SpeakerToggle } from "./SpeakerToggle";

// If the interviewer hasn't joined by now, something went wrong on the worker side. It usually
// joins within a few seconds.
const AGENT_JOIN_TIMEOUT_MS = 15_000;
// Reassure the visitor while they wait.
const AGENT_SLOW_MS = 6000;
// Show "say hello" if the interviewer hasn't spoken this long after joining.
const GREETING_HINT_MS = 12_000;
// Backup for a lost "demo.ended" message.
const END_GRACE_MS = 8000;

export type CallEndReason = "time_up" | "user";

type Props = {
  maxSeconds: number;
  questions: string[];
  company: string;
  stream: MediaStream | null;
  cameraOn: boolean;
  // Android phones: the loudspeaker and earpiece routes, for the speaker switch.
  phoneAudio: PhoneAudio | null;
  onToggleCamera: () => void;
  onEnded: (reason: CallEndReason) => void;
  // The interviewer never joined: timed out, or the visitor hung up while waiting.
  onNoAgent: () => void;
  onTranscript: (rows: TranscriptRow[]) => void;
  secondsLeftRef: React.MutableRefObject<number>;
};

const formatClock = (seconds: number) => {
  const clamped = Math.max(Math.ceil(seconds), 0);
  return `${Math.floor(clamped / 60)}:${String(clamped % 60).padStart(2, "0")}`;
};

const stateLabel = (state: string) => {
  switch (state) {
    case "speaking": {
      return "Speaking";
    }
    case "thinking": {
      return "Thinking";
    }
    case "listening": {
      return "Listening";
    }
    default: {
      return "Joining";
    }
  }
};

export const DemoCall = ({
  maxSeconds,
  questions,
  company,
  stream,
  cameraOn,
  phoneAudio,
  onToggleCamera,
  onEnded,
  onNoAgent,
  onTranscript,
  secondsLeftRef,
}: Props) => {
  const { agent, state, audioTrack } = useVoiceAssistant();
  const { localParticipant, isMicrophoneEnabled } = useLocalParticipant();
  const { canPlayAudio, startAudio } = useAudioPlayback();
  const transcriptions = useTranscriptions();

  const [endsAt, setEndsAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [agentJoinedAt, setAgentJoinedAt] = useState<number | null>(null);
  const endedRef = useRef(false);
  const agentJoinedRef = useRef(false);
  const mountedAtRef = useRef(Date.now());

  const end = (reason: CallEndReason) => {
    if (endedRef.current) return;
    endedRef.current = true;
    // Hanging up before the interviewer arrived: there is nothing to review.
    if (!agentJoinedRef.current) onNoAgent();
    else onEnded(reason);
  };

  useDataChannel("practice-demo", (message) => {
    try {
      const event = JSON.parse(new TextDecoder().decode(message.payload)) as {
        type: string;
        maxSeconds?: number;
      };
      if (event.type === "demo.started") {
        setEndsAt(Date.now() + (event.maxSeconds ?? maxSeconds) * 1000);
      } else if (event.type === "demo.ended") {
        end("time_up");
      }
    } catch {
      // Ignore messages we don't understand.
    }
  });

  // Start the clock when the interviewer joins, in case "demo.started" never arrives.
  useEffect(() => {
    if (!agent || agentJoinedAt) return;
    agentJoinedRef.current = true;
    const joined = Date.now();
    setAgentJoinedAt(joined);
    setEndsAt((current) => current ?? joined + maxSeconds * 1000);
  }, [agent, agentJoinedAt, maxSeconds]);

  // Measured from when the call opened, so a re-render can't restart the wait.
  const onNoAgentRef = useRef(onNoAgent);
  onNoAgentRef.current = onNoAgent;
  useEffect(() => {
    if (agent) return;
    const waited = Date.now() - mountedAtRef.current;
    const timeout = window.setTimeout(() => {
      if (endedRef.current || agentJoinedRef.current) return;
      endedRef.current = true;
      onNoAgentRef.current();
    }, Math.max(AGENT_JOIN_TIMEOUT_MS - waited, 0));
    return () => window.clearTimeout(timeout);
  }, [agent]);

  useEffect(() => {
    const interval = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(interval);
  }, []);

  const secondsLeft = endsAt ? (endsAt - now) / 1000 : maxSeconds;
  secondsLeftRef.current = secondsLeft;

  useEffect(() => {
    if (endsAt && now > endsAt + END_GRACE_MS) end("time_up");
  });

  // Transcripts arrive as short fragments. Group consecutive fragments from the same speaker
  // into one row, keeping each fragment's text as received.
  const rows = useMemo(() => {
    const start = agentJoinedAt ?? mountedAtRef.current;
    const grouped: TranscriptRow[] = [];
    for (const item of transcriptions) {
      if (item.text.trim().length === 0) continue;
      const speaker =
        item.participantInfo.identity === localParticipant.identity ? "candidate" : "agent";
      const last = grouped.at(-1);
      if (last?.speaker === speaker) {
        const needsSpace = !/\s$/.test(last.text) && !/^[\s.,!?;:]/.test(item.text);
        last.text = `${last.text}${needsSpace ? " " : ""}${item.text}`;
      } else {
        grouped.push({
          id: item.streamInfo.id,
          speaker,
          text: item.text.trimStart(),
          at: Math.max((item.streamInfo.timestamp - start) / 1000, 0),
        });
      }
    }
    return grouped;
  }, [transcriptions, localParticipant.identity, agentJoinedAt]);

  useEffect(() => {
    onTranscript(rows);
  }, [rows, onTranscript]);

  const waitingForAgent = agentJoinedAt === null;
  const agentSlow = waitingForAgent && now - mountedAtRef.current > AGENT_SLOW_MS;
  const agentHasSpoken = rows.some((row) => row.speaker === "agent");
  const showHelloHint =
    !agentHasSpoken && agentJoinedAt !== null && now - agentJoinedAt > GREETING_HINT_MS;

  // Follow new captions only while the reader is already at the bottom.
  const logRef = useRef<HTMLOListElement>(null);
  const pinnedRef = useRef(true);
  useLayoutEffect(() => {
    const log = logRef.current;
    if (log && pinnedRef.current) log.scrollTop = log.scrollHeight;
  }, [rows]);

  const lowTime = endsAt !== null && secondsLeft <= 30;
  const demoMinutes = Math.round(maxSeconds / 60);

  return (
    <>
      {/* Stays at the top of the screen for the whole call: this is a short, timed demo. */}
      <div className={`a25-timer-banner${lowTime ? " is-low" : ""}`}>
        <span className="a25-timer-kind">
          <span className="a25-live-dot" aria-hidden="true" />
          {demoMinutes}-minute demo
        </span>
        <span className="a25-timer-count">
          <span className="a25-timer-label">
            {endsAt === null ? "Starts when the interviewer joins" : "Time remaining in the demo"}
          </span>
          <span
            className="a25-timer-clock"
            role="timer"
            aria-label={`${formatClock(secondsLeft)} remaining in the demo`}
          >
            {formatClock(secondsLeft)}
          </span>
        </span>
      </div>

      <div className="a25-call">
        <RoomAudioRenderer />

        <div className="a25-call-main">
          <div className="a25-call-top">
            <span className="a25-call-title">{company} interview</span>
          </div>

          <div className="a25-call-tiles">
            <div className="a25-agent-tile" data-state={state}>
              <BarVisualizer
                className="a25-visualizer"
                state={state}
                track={audioTrack}
                barCount={5}
                options={{ minHeight: 12 }}
              />
              <span className="a25-tile-label">
                AI interviewer <span aria-live="polite">· {stateLabel(state)}</span>
              </span>
            </div>
            <SelfView stream={stream} cameraOn={cameraOn} />
          </div>

          <div className="a25-captions">
            {waitingForAgent && (
              <p className="a25-captions-empty" role="status">
                <Loader2 size={14} className="a25-spin" aria-hidden="true" />{" "}
                {agentSlow
                  ? "Still waiting for the interviewer. This usually takes a few seconds…"
                  : "Waiting for the interviewer to join…"}
              </p>
            )}
            {showHelloHint && <p className="a25-hint">Say hello to begin.</p>}
            {rows.length === 0 && !showHelloHint && !waitingForAgent && (
              <p className="a25-captions-empty">Captions appear here as you talk.</p>
            )}
            <ol
              ref={logRef}
              className="a25-caption-log"
              role="log"
              aria-label="Live captions"
              onScroll={(event) => {
                const el = event.currentTarget;
                pinnedRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
              }}
            >
              {rows.map((row) => (
                <li key={row.id} data-speaker={row.speaker}>
                  <span>{row.speaker === "candidate" ? "You" : "Interviewer"}</span>
                  <p>{row.text}</p>
                </li>
              ))}
            </ol>
          </div>

          <div className="a25-controls" role="group" aria-label="Call controls">
            {!canPlayAudio && (
              <button
                type="button"
                className="a25-control is-wide"
                onClick={() => void startAudio()}
              >
                <Volume2 size={17} aria-hidden="true" /> Tap to hear the interviewer
              </button>
            )}
            <SpeakerToggle phoneAudio={phoneAudio} className="a25-control is-speaker" />
            <button
              type="button"
              className="a25-control"
              aria-pressed={!isMicrophoneEnabled}
              aria-label={isMicrophoneEnabled ? "Mute microphone" : "Unmute microphone"}
              onClick={() => void localParticipant.setMicrophoneEnabled(!isMicrophoneEnabled)}
            >
              {isMicrophoneEnabled ? <Mic size={17} /> : <MicOff size={17} />}
            </button>
            <button
              type="button"
              className="a25-control"
              aria-pressed={!cameraOn}
              aria-label={cameraOn ? "Turn camera off" : "Turn camera on"}
              onClick={onToggleCamera}
            >
              {cameraOn ? <Video size={17} /> : <VideoOff size={17} />}
            </button>
            <button type="button" className="a25-control is-end" onClick={() => end("user")}>
              <PhoneOff size={17} aria-hidden="true" /> End interview
            </button>
          </div>
        </div>

        <aside className="a25-call-questions" aria-label="Interview questions">
          <p className="a25-label">Your questions</p>
          <ol>
            {questions.map((question, index) => (
              <li key={question}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                {question}
              </li>
            ))}
          </ol>
          <p className="a25-note">Answer out loud, like a real interview. About 2 minutes.</p>
        </aside>
      </div>
    </>
  );
};
